// MFA enrollment page — voor authenticated users (vanuit /settings of after-login).
// PRD-01 §9.2: QR scan → 1× TOTP bevestigen → 10 backup-codes ontvangen.

import { useState, useEffect, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/api";

interface EnrollResponse {
  otpauthUri: string;
  qrDataUri: string;
}

interface ConfirmResponse {
  backupCodes: string[];
}

export function MfaEnrollPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [qr, setQr] = useState<EnrollResponse | null>(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .post<EnrollResponse>("/v1/auth/mfa/enroll")
      .then(setQr)
      .catch((e) => setErrorKey(e instanceof ApiError ? e.i18nKey : "errors.internal"));
  }, []);

  async function onConfirm(e: FormEvent) {
    e.preventDefault();
    setErrorKey(null);
    setLoading(true);
    try {
      const data = await api.post<ConfirmResponse>("/v1/auth/mfa/confirm-enrollment", { code });
      setBackupCodes(data.backupCodes);
    } catch (err) {
      setErrorKey(err instanceof ApiError ? err.i18nKey : "errors.internal");
    } finally {
      setLoading(false);
    }
  }

  if (backupCodes) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-brand-50 to-white">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8">
          <h1 className="text-2xl font-semibold mb-2">
            {t("auth.mfa.enroll.done", "MFA geactiveerd")}
          </h1>
          <p className="text-sm text-neutral-700 mb-4">
            {t(
              "auth.mfa.enroll.backupBody",
              "Bewaar deze 10 backup-codes op een veilige plek. Elke code is eenmalig bruikbaar als je geen toegang hebt tot je authenticator-app."
            )}
          </p>
          <div className="grid grid-cols-2 gap-2 mb-6 font-mono text-sm">
            {backupCodes.map((c) => (
              <div key={c} className="px-3 py-2 bg-neutral-50 border border-neutral-200 rounded">
                {c}
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate("/dashboard", { replace: true })}
            className="w-full px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-lg"
          >
            {t("auth.mfa.enroll.continue", "Doorgaan naar dashboard")}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-brand-50 to-white">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8">
        <h1 className="text-2xl font-semibold mb-2">
          {t("auth.mfa.enroll.title", "Tweestapsverificatie instellen")}
        </h1>
        <p className="text-sm text-neutral-600 mb-6">
          {t(
            "auth.mfa.enroll.subtitle",
            "Scan de QR-code met je authenticator-app (Google Authenticator, Authy, 1Password) en voer daarna de eerste 6-cijferige code in."
          )}
        </p>

        {qr ? (
          <div className="space-y-4">
            <img
              src={qr.qrDataUri}
              alt={t("auth.mfa.enroll.qrAlt", "MFA QR-code")}
              className="mx-auto w-48 h-48 border border-neutral-200 rounded-lg"
            />
            <details className="text-xs text-neutral-500">
              <summary className="cursor-pointer">
                {t("auth.mfa.enroll.manualKey", "Handmatig invoeren?")}
              </summary>
              <code className="block mt-1 break-all">{qr.otpauthUri}</code>
            </details>

            <form onSubmit={onConfirm} className="space-y-4" aria-busy={loading}>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                pattern="\d{6}"
                className="w-full px-3 py-2 text-lg font-mono tracking-widest text-center border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                placeholder="123456"
              />
              {errorKey && (
                <div
                  role="alert"
                  className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800"
                >
                  {t(errorKey)}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg"
              >
                {loading
                  ? t("auth.mfa.enroll.confirming", "Bevestigen…")
                  : t("auth.mfa.enroll.confirm", "Activeren")}
              </button>
            </form>
          </div>
        ) : errorKey ? (
          <div role="alert" className="px-3 py-2 rounded-lg bg-red-50 text-sm text-red-800">
            {t(errorKey)}
          </div>
        ) : (
          <p className="text-sm text-neutral-500">{t("auth.mfa.enroll.loading", "Laden…")}</p>
        )}
      </div>
    </main>
  );
}
